import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReplayIcon from "@mui/icons-material/Replay";
import VideoFileOutlinedIcon from "@mui/icons-material/VideoFileOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { usePageHeader } from "../../context/LayoutContext";
import { useJobStore } from "../../store/jobStore";
import { jobStatusQuery } from "../../features/jobs/api/jobQueries";
import {
  useTranscriptionFailureActions,
  FailedVideoList,
  TRANSCRIPTION_FAILURE_TITLE,
  TRANSCRIPTION_FAILURE_BODY,
  CONTINUE_LABEL,
  CANCEL_LABEL,
} from "../../features/jobs/transcription-failure";
import { deriveGenerationView } from "./deriveGenerationView";
import { EmptyState, ErrorState } from "../../shared/ui";
import {
  CountdownRing,
  GENERATION_OVERTIME_MESSAGE,
  ProgressBar,
  useGenerationCountdown,
} from "../../components/jobs/components";
import { routeForReport } from "../../shared/routing/routeForReport";
import { ReportStatus } from "../../shared/interfaces/ReportStatus";
import { useHitlEnabled } from "@/shared/hooks/useHitlEnabled";
import {
  getAwaitingInputActionLabel,
  getAwaitingInputDescription,
  getAwaitingInputRoute,
} from "../../features/hitl/presentPlan";
import { getAwaitingInputSurfaceStyles } from "@/shared/utils/awaitingInputStyles";

/**
 * The single generation surface: deep-link/refresh-safe (everything derives
 * from the jobId param, the shared job-status query, and the persisted
 * registry). Shares its query key with the useActiveJobStatusSync fan-out,
 * so mounting this page adds an observer to the existing 3s loop instead of
 * a second poller. This page never writes to the registry — the fan-out
 * owns registry writes.
 */
export const GeneratingPage: React.FC = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const hitlEnabled = useHitlEnabled();
  const [retrying, setRetrying] = useState(false);

  const jobs = useJobStore((state) => state.jobs);
  const resumedJobs = useJobStore((state) => state.resumedJobs);
  const retrySingleJob = useJobStore((state) => state.retrySingleJob);
  const registryJob = useMemo(
    () => jobs.find((job) => job.id === jobId),
    [jobs, jobId],
  );

  // The registry entry carries its own resume stamp, but a job this device
  // never registered (approval opened from a link, started elsewhere) has no
  // entry at all — the store's jobId-keyed ledger covers it, so the officer
  // isn't sent back to the prompt they just answered.
  const resumedAt =
    registryJob?.resumedAt ?? (jobId ? resumedJobs[jobId] : undefined);

  const statusQuery = useQuery({
    ...jobStatusQuery(jobId ?? "", { resumedAt }),
    enabled: !!jobId,
  });

  const view = deriveGenerationView(
    registryJob,
    statusQuery.data,
    statusQuery.error,
    resumedAt,
  );

  // The transcript-decision prompt reads the failed-video list straight from
  // the /jobStatus snapshot the page already polls — no extra fetch.
  const transcriptDecision =
    view.kind === "transcript-decision"
      ? {
          jobId: view.jobId,
          reportId: view.reportId,
          failedVideoIds: view.failedVideoIds,
          message: view.message,
        }
      : null;
  const {
    onContinue,
    onCancel,
    busy: promptBusy,
  } = useTranscriptionFailureActions(transcriptDecision, {
    onCancelled: () => navigate("/"),
  });

  // Drives the narrative-only countdown. Called unconditionally (hooks rule);
  // its output is only consumed when the view is narrative-only progress.
  const countdown = useGenerationCountdown(
    registryJob?.displayStartedAt ?? registryJob?.createdAt ?? 0,
  );

  usePageHeader({
    title: "Generating Report",
    subtitle: "Your report is being drafted from the selected evidence",
    icon: <BoltIcon />,
  });

  // Generation is no longer a guided wizard step — this surface is reached from
  // the Report Tracker on My Reports. Offer a plain way back instead of the
  // (now 2-step) wizard stepper.
  const stepperRow = (
    <Box sx={{ mb: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        color="inherit"
        onClick={() => navigate("/")}
        sx={{ color: "text.secondary" }}
      >
        My Reports
      </Button>
    </Box>
  );

  if (!jobId || view.kind === "unknown") {
    return (
      <Container maxWidth="md" sx={{ py: 2 }}>
        {stepperRow}
        <EmptyState
          variant="panel"
          icon={<PendingActionsIcon />}
          title="This report job isn't available on this device"
          description="It may have been started on another device or already completed. Finished reports appear on the My Reports page."
          action={
            <Button variant="contained" onClick={() => navigate("/")}>
              Go to My Reports
            </Button>
          }
        />
      </Container>
    );
  }

  if (view.kind === "failed") {
    return (
      <Container maxWidth="md" sx={{ py: 2 }}>
        {stepperRow}
        <ErrorState
          variant="panel"
          message={view.message}
          {...(view.canRetry && registryJob
            ? {
                onRetry: async () => {
                  if (retrying) return;
                  setRetrying(true);
                  await retrySingleJob(registryJob);
                  setRetrying(false);
                },
                retryLabel: "Try again",
              }
            : {})}
        />
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Button
            startIcon={<ReplayIcon />}
            onClick={() => navigate("/video-selection")}
          >
            Start a new report
          </Button>
        </Box>
      </Container>
    );
  }

  if (view.kind === "ready") {
    return (
      <Container maxWidth="md" sx={{ py: 2 }}>
        {stepperRow}
        <Paper
          variant="outlined"
          sx={{ p: 4, textAlign: "center", borderColor: "divider" }}
        >
          <CheckCircleOutlineIcon
            sx={{ fontSize: 56, color: "success.main", mb: 1 }}
          />
          <Typography variant="h5" sx={{ mb: 1 }}>
            Your report is ready for review
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Open it to review the extracted information, make edits, and sign.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="contained"
              startIcon={<OpenInNewIcon />}
              onClick={() =>
                navigate(
                  routeForReport(
                    { id: view.reportId, status: ReportStatus.PENDING_REVIEW },
                    // Same flags handleJobClick uses for fresh completions.
                    { coApprovalEnabled: false, hitlEnabled, isCoView: false },
                  ),
                  {
                    state: {
                      sparseInfo: registryJob?.sparseInfo,
                      fromJobMonitor: true,
                    },
                  },
                )
              }
            >
              Open report
            </Button>
            <Button color="inherit" onClick={() => navigate("/")}>
              Back to My Reports
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  if (view.kind === "transcript-decision") {
    const surface = getAwaitingInputSurfaceStyles(theme);
    return (
      <Container maxWidth="md" sx={{ py: 2 }}>
        {stepperRow}
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            backgroundColor: surface.backgroundColor,
            borderColor: surface.borderColor,
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <PendingActionsIcon
              sx={{ fontSize: 56, color: surface.accentColor, mb: 1 }}
            />
            {/* Deliberately not the server's prompt message: it says the report
                can be generated "without them", which reads as the footage being
                dropped. Only the transcript is missing — the video is still
                analyzed — so this surface owns the wording. */}
            <Typography variant="h5" sx={{ mb: 1 }}>
              {TRANSCRIPTION_FAILURE_TITLE}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {TRANSCRIPTION_FAILURE_BODY}
            </Typography>
          </Box>
          <FailedVideoList videoIds={view.failedVideoIds} />
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            justifyContent="center"
            sx={{ mt: 3 }}
          >
            <Button variant="contained" onClick={onContinue} loading={promptBusy}>
              {CONTINUE_LABEL}
            </Button>
            <Button color="inherit" onClick={onCancel} disabled={promptBusy}>
              {CANCEL_LABEL}
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  if (view.kind === "awaiting-input") {
    const surface = getAwaitingInputSurfaceStyles(theme);
    return (
      <Container maxWidth="md" sx={{ py: 2 }}>
        {stepperRow}
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            textAlign: "center",
            backgroundColor: surface.backgroundColor,
            borderColor: surface.borderColor,
          }}
        >
          <PendingActionsIcon
            sx={{ fontSize: 56, color: surface.accentColor, mb: 1 }}
          />
          <Typography variant="h5" sx={{ mb: 1 }}>
            Your input is needed to continue
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {getAwaitingInputDescription(hitlEnabled)}
          </Typography>
          <Button
            variant="contained"
            onClick={() =>
              navigate(getAwaitingInputRoute(view.targetId, hitlEnabled))
            }
          >
            {getAwaitingInputActionLabel(hitlEnabled)}
          </Button>
        </Paper>
      </Container>
    );
  }

  // "progress" and "loading"
  const progress = view.kind === "progress" ? view.progress : 0;
  const message = view.kind === "progress" ? view.message : undefined;
  const videoTitles = view.kind === "progress" ? view.videoTitles : undefined;
  const narrativeOnly = view.kind === "progress" && !!view.narrativeOnly;
  // Narrative-only has no server percentage — the countdown's elapsed fraction
  // fills the ring/bar while the mm:ss readout counts down.
  const barProgress = narrativeOnly ? countdown.elapsedPct : progress;
  // The budgeted window drained before the job finished: retire the timer and
  // say so, rather than parking the readout at 0:00.
  const overtime = narrativeOnly && countdown.expired;

  return (
    <Container maxWidth="md" sx={{ py: 2 }}>
      {stepperRow}
      <Paper
        variant="outlined"
        sx={{ p: 4, textAlign: "center", borderColor: "divider" }}
      >
        <Box sx={{ mb: 2 }}>
          {narrativeOnly ? (
            <CountdownRing
              value={countdown.elapsedPct}
              label={countdown.label}
              size={88}
              thickness={3}
              expired={overtime}
            />
          ) : (
            <Box sx={{ position: "relative", display: "inline-flex" }}>
              <CircularProgress
                variant={progress > 0 ? "determinate" : "indeterminate"}
                value={progress}
                size={88}
                thickness={3}
              />
              {progress > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="subtitle1" component="div">
                    {`${Math.round(progress)}%`}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
        <Typography variant="h5" sx={{ mb: 1 }}>
          Generating your report
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ minHeight: 20 }}>
          {overtime
            ? GENERATION_OVERTIME_MESSAGE
            : message ||
              (narrativeOnly
                ? "Drafting the narrative from the selected evidence…"
                : "Processing the selected evidence…")}
        </Typography>
        <Box sx={{ maxWidth: 420, mx: "auto" }}>
          <ProgressBar progress={barProgress} indeterminate={overtime} />
        </Box>
        {!!videoTitles?.length && (
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            justifyContent="center"
            sx={{ mt: 3 }}
          >
            {videoTitles.map((title, index) => (
              <Chip
                key={`${title}-${index}`}
                size="small"
                icon={<VideoFileOutlinedIcon />}
                label={title}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                }}
              />
            ))}
          </Stack>
        )}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 3 }}
        >
          You can leave this page — generation continues in the background and
          the Report Tracker will notify you when it finishes.
        </Typography>
      </Paper>
    </Container>
  );
};

export default GeneratingPage;
